import { Injectable, ComponentRef, Injector, NgZone } from '@angular/core';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ToastComponent } from 'app/core/components/toast/toast.component';
import { HttpErrorResponse } from '@angular/common/http';

type ToastType = 'error' | 'warning' | 'success' | 'info';

export interface ToastOptions {
  tag?: string | null;
  classTheme?: string;
  content?: string;
  actionText?: string;
  autoHide?: boolean;
  duration?: number;
}

export const defaultToastOptions: ToastOptions = {
  tag: null,
  classTheme: 'motif-theme-dark',
  content: '',
  actionText: '',
  autoHide: true,
  duration: 3000,
};

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  readonly HTTP_ERROR_TAG = 'httpError';

  private overlayRef: OverlayRef | null = null;
  private toastRef?: ComponentRef<ToastComponent>;
  private toastTag?: string | null;

  constructor(
    private overlay: Overlay,
    private injector: Injector,
    private ngZone: NgZone
  ) {}

  showHttpResponseErrorMessage(
    errorResponse?: HttpErrorResponse,
    defaultMessage: string = 'An unexpected error occurred.',
    toastOptions: ToastOptions = {}
  ): void {
    const message: string = errorResponse?.error?.message || defaultMessage;

    this.showToast('error', message, {
      ...{
        tag: this.HTTP_ERROR_TAG,
        classTheme: '',
        autoHide: false,
      },
      ...toastOptions,
    });
  }

  // Function to show the toast
  showToast(
    type: ToastType,
    message: string,
    options: ToastOptions = {}
  ): Promise<boolean> {
    options = { ...defaultToastOptions, ...options };

    return new Promise((resolve) => {
      // if there is a previous toast, get rid of it to
      // show a new one and avoid errors
      this.clearCurrentToast();

      if (!this.overlayRef) {
        this.overlayRef = this.overlay.create({
          hasBackdrop: false,
          positionStrategy: this.getToastPosition(),
          panelClass: 'toast-panel',
        });
      }

      // Create a portal for the ToastComponent
      const toastPortal = new ComponentPortal(
        ToastComponent,
        null,
        this.injector
      );
      const toastRef: ComponentRef<ToastComponent> =
        this.overlayRef.attach(toastPortal);

      // Set the input properties for the ToastComponent
      toastRef.instance.classTheme = options.classTheme!;
      toastRef.instance.autoHide = options.autoHide!;
      toastRef.instance.type = type;
      toastRef.instance.message = message;
      toastRef.instance.content = options.content!;
      toastRef.instance.actionText = options.actionText!;
      toastRef.instance.show = true;

      // **Ensure toast is focused after being fully added to the DOM**
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          const toastElement = toastRef.instance.elementRef.nativeElement;

          if (toastElement) {
            toastElement.setAttribute('tabindex', '0'); // Ensure it's focusable
            toastElement.focus();
          }
        }, 100); // Delay to ensure overlay is fully rendered
      });

      this.toastRef = toastRef;

      this.toastTag = options.tag;

      // Automatically hide the toast after the specified duration
      if (options.autoHide) {
        setTimeout(() => {
          this.clearCurrentToast();
        }, options.duration);
      } else {
        toastRef.instance.hidden.subscribe(() => {
          this.clearCurrentToast();
          resolve(false);
        });

        toastRef.instance.onaction.subscribe(() => {
          this.clearCurrentToast();
          resolve(true);
        });
      }
    });
  }

  // Function to define the position of the toast
  private getToastPosition(): PositionStrategy {
    return this.overlay.position().global().centerHorizontally().top('10px');
  }

  private clearCurrentToast(): void {
    if (this.toastRef) {
      this.toastRef.instance.show = false;
    }
    this.overlayRef?.detach();
  }

  /**
   * Clear last toast, if it has the specified tag.
   */
  clearToastWithTag(tag: string | boolean): void {
    if (typeof tag === 'boolean' ? tag : this.toastTag == tag) {
      this.clearCurrentToast();
    }
  }
}
